import { Component, ViewChild, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, merge, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { User } from 'src/app/models/user.model';
import { FlaggedUser, MessageFlagReason } from 'src/app/models/message-flag.model';
import { MessageFlagsService } from 'src/app/services/message-flags.service';
import { UsersService } from 'src/app/services/users.service';
import { ConfirmationService } from 'src/app/lib/services/confirmation.service';
import { ToastsService } from 'src/app/lib/services/toasts.service';
import { TimezoneService } from 'src/app/services/timezone.service';
import { UserDialogComponent } from '../user-dialog/user-dialog.component';

/**
 * "Message Flags": senders whose outgoing chat messages tripped a spam/scam
 * rule — Telegram/WhatsApp pushes, investment or crypto pitches, money
 * requests, or the same opener mass-sent to many matches.
 *
 * Rows render through the shared users table, so the photo strip and every
 * moderation action are exactly what /users shows; the API returns each row as
 * a user object with the flag aggregates attached. The flag column drills into
 * the per-message breakdown.
 */
@Component({
  standalone: false,
  selector: 'sa-message-flags',
  templateUrl: './message-flags.component.html',
  styleUrls: ['./message-flags.component.scss'],
})
export class MessageFlagsComponent implements AfterViewInit, OnInit, OnDestroy {
  constructor(
    public dialog: MatDialog,
    private router: Router,
    private messageFlagsService: MessageFlagsService,
    private usersService: UsersService,
    private confirmationService: ConfirmationService,
    private toastsService: ToastsService,
    public timezoneService: TimezoneService,
  ) {}

  // The /users column set, in the same order, with the flag columns inserted —
  // a reviewer should not have to jump between two pages to see who a flagged
  // sender is.
  readonly displayedColumns: string[] = [
    'profile_image', 'actions', 'flag_details', 'user_id', 'name', 'email',
    'birth', 'gender', 'media', 'country', 'platform', 'login_method',
    'top_reasons', 'flag_count', 'flagged_message_count', 'distinct_recipients',
    'report_count', 'like_dislike_ratio', 'given_like_dislike_ratio',
    'createdAt', 'last_activity', 'active', 'subscription_tier',
    'last_flagged_at',
  ];

  readonly initialSort: Sort = { active: 'flag_count', direction: 'desc' };
  readonly pageSizeOptions = [30, 100, 200, 500];

  query = new BehaviorSubject<string>('');
  reason = new BehaviorSubject<string>('');
  reportedOnly = new BehaviorSubject<boolean>(false);
  sortChange = new BehaviorSubject<Sort>(this.initialSort);

  reasons: MessageFlagReason[] = [];
  data: FlaggedUser[] = [];

  resultsLength = 0;
  isLoading = true;
  hasError = false;
  limit = 30;

  timezoneAbbr = 'PST';
  private timezoneSubscription: Subscription | null = null;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  ngOnInit() {
    this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    this.timezoneSubscription = this.timezoneService.getTimezoneConfig().subscribe(() => {
      this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    });

    // The filter offers whatever rules the API currently ships; an empty list
    // just means the dropdown stays on "All reasons".
    this.messageFlagsService.getReasons().pipe(
      catchError(() => of({ reasons: [] as MessageFlagReason[] })),
    ).subscribe(response => this.reasons = response.reasons || []);
  }

  ngAfterViewInit() {
    merge(this.paginator.page, this.sortChange, this.query, this.reason, this.reportedOnly)
      .pipe(
        withLatestFrom(this.sortChange),
        switchMap(([, sort]) => {
          this.isLoading = true;
          return this.messageFlagsService.getFlaggedUsers(
            this.query.value,
            this.paginator.pageIndex + 1,
            this.paginator.pageSize || this.limit,
            `${sort.active}:${sort.direction}`,
            this.reason.value,
            this.reportedOnly.value,
          );
        }),
        map((collection) => {
          this.isLoading = false;
          this.hasError = false;
          this.resultsLength = collection.totalCount;
          return collection.results;
        }),
        catchError(() => {
          this.isLoading = false;
          this.hasError = true;
          return of([] as FlaggedUser[]);
        }),
      )
      .subscribe(data => this.data = data);
  }

  ngOnDestroy() {
    if (this.timezoneSubscription) {
      this.timezoneSubscription.unsubscribe();
    }
  }

  /** Re-runs the current query — used after an action changes a user. */
  private refresh() {
    this.query.next(this.query.value);
  }

  queryChanged(event) {
    this.paginator.pageIndex = 0;
    this.query.next(event.target.value);
  }

  reasonChanged(value: string) {
    this.paginator.pageIndex = 0;
    this.reason.next(value);
  }

  reportedOnlyChanged(checked: boolean) {
    this.paginator.pageIndex = 0;
    this.reportedOnly.next(checked);
  }

  sortChanged(sort: Sort) {
    this.sortChange.next(sort);
  }

  pageChanged(event) {
    this.paginator.pageIndex = +event.target.value - 1;
    this.refresh();
  }

  /** The flag column's drill-down: every flagged message for this sender. */
  flagDetailsClicked(user: User) {
    this.router.navigate(['/message-flags', user.user_id]);
  }

  userClicked(user: User) {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: { user },
      minWidth: 400,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'edit') {
        this.router.navigate(['/users', user.user_id]);
      }
    });
  }

  userBanClicked(user: User) {
    this.usersService.banUser(user).pipe(
      tap(() => {
        this.toastsService.showSuccess('Banned user successfully.');
        this.refresh();
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error banning user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userUnBanClicked(user: User) {
    this.confirmationService.askForConfirmation(`Unban ${user.name} ${user.lastname}?`, `Are you sure you want to unban ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.unBanUser(user).pipe(
          tap(() => {
            this.toastsService.showSuccess('Unbanned user successfully.');
            this.refresh();
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error unbanning user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userPutUnderReviewClicked(user: User) {
    this.usersService.putUserUnderReview(user).pipe(
      tap(() => {
        this.toastsService.showSuccess('Put user under review successfully.');
        this.refresh();
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error putting user under review: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userRemoveFromReviewClicked(user: User) {
    this.confirmationService.askForConfirmation(`Remove ${user.name} ${user.lastname} from review?`, `Are you sure you want to remove ${user.name} ${user.lastname} from review?`)
      .then(() => {
        this.usersService.removeUserFromReview(user).pipe(
          tap(() => {
            this.toastsService.showSuccess('Removed user from review successfully.');
            this.refresh();
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error removing user from review: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userSilentHideClicked(user: User) {
    this.usersService.silentHideUser(user).pipe(
      tap(() => {
        this.toastsService.showSuccess('Silent hid user successfully.');
        this.refresh();
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error silent hiding user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userSilentUnhideClicked(user: User) {
    this.confirmationService.askForConfirmation(`Unhide ${user.name} ${user.lastname}?`, `Are you sure you want to unhide ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.silentUnhideUser(user).pipe(
          tap(() => {
            this.toastsService.showSuccess('Unhid user successfully.');
            this.refresh();
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error unhiding user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userBoostClicked(user: User) {
    this.usersService.boostUser(user).pipe(
      tap(() => this.toastsService.showSuccess('Boosted user successfully.')),
      catchError((error) => {
        this.toastsService.showDanger('Error boosting user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userDeboostClicked(user: User) {
    this.usersService.deboostUser(user).pipe(
      tap(() => this.toastsService.showSuccess('Deboost user successfully.')),
      catchError((error) => {
        this.toastsService.showDanger('Error deboosting user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userDeleteClicked(user: User) {
    this.confirmationService.askForConfirmation(`Delete ${user.name} ${user.lastname}?`, `Are you sure you want to delete ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.deleteUser(user).pipe(
          tap(() => {
            this.toastsService.showSuccess('Deleted user successfully.');
            this.refresh();
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error deleting user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }
}
